import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: React.ReactNode;
  confirmButtonColor?: 'primary' | 'error';
}

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 450,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  outline: 'none',
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonColor = 'error',
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const handleConfirm = async () => {
    try {
      const result = onConfirm();

      if (result instanceof Promise) {
        setLoading(true);
        await result;
      }
      onClose();
    } catch (err) {
      console.error('Confirmation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Box sx={style}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningAmberIcon color="warning" sx={{ fontSize: 32 }} />
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            disabled={loading}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ p: 3 }}>
          <Typography sx={{ mb: 4 }}>{message}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              color={confirmButtonColor}
              onClick={handleConfirm}
              disabled={loading}
              startIcon={
                loading && <CircularProgress size={16} color="inherit" />
              }
            >
              {loading ? 'Processing...' : 'Proceed'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmationModal;
